import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftIcon, BadgeCheckIcon, FileTextIcon, LockIcon, PaperclipIcon, SendIcon, ShieldCheckIcon, StickyNoteIcon, TruckIcon } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Panel } from '../ui/Panel';
import { Textarea } from '../ui/Field';
import { RequestTimeline } from './RequestTimeline';
import { AuthorisationPanel } from './AuthorisationPanel';
import { naira } from '../../utils/format';
import { ORIGIN_LABEL, ORIGIN_TONE, PAYEE_LABEL, feeTotals, statusTone } from '../../utils/services';
import { academicServices, serviceInstitutions, serviceProviders } from '../../data/services';
import { useNotes } from '../../contexts/NotesContext';
import type { RequestMessage, ServiceRequest, ShareScope } from '../../types/services';
export function RequestDetail({
  request,
  onBack,
  onUpdate
}: {
  request: ServiceRequest;
  onBack: () => void;
  onUpdate: (patch: Partial<ServiceRequest>) => void;
}) {
  const [draft, setDraft] = useState('');
  const {
    notes,
    createNote
  } = useNotes();
  const service = academicServices.find(item => item.id === request.serviceId)!;
  const institution = serviceInstitutions.find(item => item.id === request.institutionId);
  const provider = serviceProviders.find(item => item.id === request.providerId);
  const totals = feeTotals(request.fees);
  const linkedNotes = notes.filter(note => request.noteIds.includes(note.id));
  function toggleScope(key: string) {
    onUpdate({
      shareScope: request.shareScope.map(scope => scope.key === key && !scope.locked ? {
        ...scope,
        on: !scope.on
      } : scope)
    });
  }
  function send() {
    if (!draft.trim()) return;
    const message: RequestMessage = {
      id: `m-${Date.now()}`,
      author: 'You',
      role: 'Student',
      body: draft.trim(),
      at: '14 Aug 2026, 23:20'
    };
    onUpdate({
      messages: [...request.messages, message]
    });
    setDraft('');
  }
  return <div className="space-y-4" data-id="element-4909" data-mp="djnfyupe1nh5">
      <div data-id="element-4910" data-mp="dkgre8zal15m">
        <Button size="sm" variant="ghost" icon={<ArrowLeftIcon className="h-3.5 w-3.5" data-id="element-4912" data-mp="ii8jp43umpac" />} onClick={onBack} data-id="element-4911" data-mp="ih71mfof12j4">
          My requests
        </Button>
      </div>

      <div className="rounded-card border border-line bg-surface px-5 py-5" data-id="element-4913" data-mp="neyvvgcj90qh">
        <div className="flex flex-wrap items-center gap-2" data-id="element-4914" data-mp="sgekfidx5pdm">
          <Badge tone={statusTone(request.status)} data-id="element-4915" data-mp="wh8582w2eo2a">{request.status}</Badge>
          <Badge tone={ORIGIN_TONE[service.origin]} data-id="element-4916" data-mp="wevpzxhoyrsx">{ORIGIN_LABEL[service.origin]}</Badge>
          <span className="cz-num text-[12px] text-ink-subtle" data-id="element-4917" data-mp="11e88y75fzeq8">{request.id}</span>
        </div>
        <h2 className="mt-2.5 text-xl font-semibold tracking-tight text-ink" data-id="element-4918" data-mp="167jkgll78joh">{service.name}</h2>
        <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-4" data-id="element-4919" data-mp="1b52zhnpq10hh">
          {[{
          term: 'Institution',
          value: institution?.name ?? '—'
        }, {
          term: 'Purpose',
          value: request.purpose
        }, {
          term: 'Deadline',
          value: request.deadline ?? 'No deadline set'
        }, {
          term: 'Handled by',
          value: provider ? provider.name : request.mode === 'official_digital' ? 'You, on the official channel' : institution?.name ?? '—'
        }].map(row => <div key={row.term} data-id="element-4920" data-mp="2qp279159w7yu">
              <dt className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-4921" data-mp="2qpu3me6p29q6">
                {row.term}
              </dt>
              <dd className="mt-0.5 text-[13px] leading-6 text-ink" data-id="element-4922" data-mp="3oednfthdfybw">{row.value}</dd>
            </div>)}
        </dl>
        {provider && <p className="mt-3 rounded-lg border border-warn-100 bg-warn-50 px-3.5 py-2.5 text-[12px] leading-5 text-ink" data-id="element-4923" data-mp="3elprf0y7tye8">
            {provider.authorityNote} The document itself is issued by {service.officialProvider}, not by
            Campuzon or the provider.
          </p>}
      </div>

      <div className="grid gap-4 lg:grid-cols-3" data-id="element-4924" data-mp="65wnrmozghel">
        <div className="space-y-4 lg:col-span-2" data-id="element-4925" data-mp="654r08jngdmq">
          <Panel title="Where it is now" data-id="element-4926" data-mp="3mt9b2lxo2m6x">
            <RequestTimeline entries={request.timeline} data-id="element-4927" data-mp="3mwezdyyh1k1l" />
          </Panel>

          <Panel title="Documents" description="What has gone in, and what is still owed." data-id="element-4928" data-mp="3qxh2wrzf8i9d">
            <ul className="divide-y divide-line" data-id="element-4929" data-mp="3cyhg7pvgydfu">
              {request.documents.map(document => <li key={document.id} className="flex flex-wrap items-center gap-2 py-2.5 first:pt-0" data-id="element-4930" data-mp="9ffq5mahclb0">
                  <FileTextIcon className="h-3.5 w-3.5 shrink-0 text-ink-subtle" data-id="element-4931" data-mp="eeiut5mjspig" />
                  <span className="min-w-0 flex-1" data-id="element-4932" data-mp="egbu1zuu07gf">
                    <span className="block text-[13px] text-ink" data-id="element-4933" data-mp="3vtz6vpmxgckn">{document.label}</span>
                    {document.detail && <span className="block text-[12px] text-ink-muted" data-id="element-4934" data-mp="4muwqlvhka80">{document.detail}</span>}
                    {document.uploadedOn && <span className="cz-num block text-[12px] text-ink-subtle" data-id="element-4935" data-mp="x61xfbcnu1jy">
                        {document.uploadedOn}
                      </span>}
                  </span>
                  <Badge tone={document.state === 'required' ? 'warn' : document.state === 'issued' ? 'ok' : document.state === 'verified' ? 'ok' : 'neutral'} data-id="element-4936" data-mp="2cv9iciifntz4">
                    {document.state === 'required' ? 'Still needed' : document.state === 'uploaded' ? 'Uploaded by you' : document.state === 'verified' ? 'Checked' : 'Issued by the institution'}
                  </Badge>
                </li>)}
            </ul>
          </Panel>

          {service.requiresAuthorisation && <AuthorisationPanel request={request} onUpdate={onUpdate} data-id="element-4937" data-mp="2c0oygfyhx8f0" />}

          <Panel title="Conversation" description="Scoped to this request only. Nobody outside it can see these messages." data-id="element-4938" data-mp="2gyqhx1zu39r0">
            <ul className="space-y-3" data-id="element-4939" data-mp="2l1dax688r0ps">
              {request.messages.map(message => <li key={message.id} className={`rounded-card border px-3.5 py-3 ${message.role === 'System' ? 'border-line bg-canvas' : message.role === 'Student' ? 'border-brand-100 bg-brand-50' : 'border-line-strong bg-surface'}`} data-id="element-4940" data-mp="2pyvppd8xwa5p">
                  <p className="flex flex-wrap items-center gap-2" data-id="element-4941" data-mp="272nhq1ojll6p">
                    <span className="text-[13px] font-semibold text-ink" data-id="element-4942" data-mp="19kvrq7tnmul3">{message.author}</span>
                    <Badge tone="neutral" data-id="element-4943" data-mp="1eixaern64rw1">{message.role}</Badge>
                    <span className="cz-num text-[12px] text-ink-subtle" data-id="element-4944" data-mp="171il6ai40ha0">{message.at}</span>
                  </p>
                  <p className="mt-1 text-[13px] leading-6 text-ink" data-id="element-4945" data-mp="121meu4lxwdra">{message.body}</p>
                  {message.attachment && <p className="mt-1.5 inline-flex items-center gap-1.5 text-[12px] font-medium text-brand-800" data-id="element-4946" data-mp="x0zvgz3v66k6">
                      <PaperclipIcon className="h-3.5 w-3.5" data-id="element-4947" data-mp="t3am9x1ij5w7" /> {message.attachment}
                    </p>}
                </li>)}
            </ul>

            <form onSubmit={event => {
            event.preventDefault();
            send();
          }} className="mt-3.5 border-t border-line pt-3.5" data-id="element-4948" data-mp="j9tm8bas8uk3">
              <Textarea rows={2} value={draft} onChange={event => setDraft(event.target.value)} placeholder="Reply to this request…" aria-label="Message" data-id="element-4949" data-mp="9b5j0ingik0g" />
              <div className="mt-2 flex flex-wrap items-center gap-2" data-id="element-4950" data-mp="1o8yofip3fdqw">
                <Button type="submit" size="sm" variant="primary" icon={<SendIcon className="h-3.5 w-3.5" data-id="element-4952" data-mp="1f9bl2rcszxv7" />} disabled={!draft.trim()} data-id="element-4951" data-mp="1t8b8xwshs8qb">
                  Send
                </Button>
                <p className="text-[12px] text-ink-muted" data-id="element-4953" data-mp="1jbztwz1ip4s6">
                  Never share a password here. No provider is permitted to ask for one.
                </p>
              </div>
            </form>
          </Panel>
        </div>

        <div className="space-y-4" data-id="element-4954" data-mp="vlcc9m1sv0sf">
          <Panel title="Fees" data-id="element-4955" data-mp="vmdzsq005fr1">
            <ul className="divide-y divide-line" data-id="element-4956" data-mp="10jnlpvo6n9a4">
              {request.fees.map(fee => <li key={fee.label} className="flex items-baseline justify-between gap-3 py-2 first:pt-0" data-id="element-4957" data-mp="mjwau3syq90c">
                  <span className="min-w-0" data-id="element-4958" data-mp="ripr23zmwe47">
                    <span className="block text-[13px] text-ink" data-id="element-4959" data-mp="rho5h5kzs988">{fee.label}</span>
                    <span className="block text-[12px] text-ink-subtle" data-id="element-4960" data-mp="7y8qoimrf4hh">{PAYEE_LABEL[fee.payee]}</span>
                  </span>
                  <span className="cz-num shrink-0 text-[13px] font-semibold text-ink" data-id="element-4961" data-mp="2zxg25on4py7">
                    {fee.unknown ? 'Not published' : naira(fee.amount)}
                  </span>
                </li>)}
            </ul>
            <p className="mt-2.5 flex items-baseline justify-between gap-3 border-t border-line pt-2.5" data-id="element-4962" data-mp="qw1qhc236yan">
              <span className="text-[13px] font-semibold text-ink" data-id="element-4963" data-mp="lx8cliiw159y">Total</span>
              <span className="cz-num text-[15px] font-semibold text-ink" data-id="element-4964" data-mp="lv4b178goicu">{naira(totals.total)}</span>
            </p>
            <p className="mt-1.5 text-[12px] leading-5 text-ink-muted" data-id="element-4965" data-mp="vn0c8r5ugola">
              Payment state: {request.paymentState}. Provider fees are held until you confirm completion;
              settlement and payouts run through the regulated providers named in Money.
            </p>
          </Panel>

          <Panel title="What the provider can see" description="You decide. Everything else is withheld." data-id="element-4966" data-mp="jt0c2er4tq3j">
            <ul className="space-y-2" data-id="element-4967" data-mp="f1s31jtps6qw">
              {request.shareScope.map((scope: ShareScope) => <li key={scope.key} data-id="element-4968" data-mp="a3ytqhx46wme">
                  <label className={`flex items-start gap-2.5 ${scope.locked ? 'cursor-not-allowed' : 'cursor-pointer'}`} data-id="element-4969" data-mp="54dxwgd8r4dw">
                    <input type="checkbox" checked={scope.on} disabled={scope.locked} onChange={() => toggleScope(scope.key)} className="mt-0.5 h-4 w-4 shrink-0 accent-brand-700" data-id="element-4970" data-mp="xtb2xhsfz4op" />
                    <span className="min-w-0" data-id="element-4971" data-mp="3dkp7jiyxudyn">
                      <span className="flex flex-wrap items-center gap-1.5" data-id="element-4972" data-mp="3dlh2crkpo7nv">
                        <span className="text-[13px] text-ink" data-id="element-4973" data-mp="38kb98sn52idt">{scope.label}</span>
                        {scope.locked && <Badge tone="neutral" icon={<LockIcon className="h-3 w-3" data-id="element-4975" data-mp="1t24ys3wb8irs" />} data-id="element-4974" data-mp="1o0z25uzj4bcz">
                            Never shared
                          </Badge>}
                      </span>
                      {scope.lockedReason && <span className="mt-0.5 block text-[12px] leading-5 text-ink-muted" data-id="element-4976" data-mp="22szsftbvykn9">
                          {scope.lockedReason}
                        </span>}
                    </span>
                  </label>
                </li>)}
            </ul>
          </Panel>

          {request.collection && <Panel title="Collection and delivery" data-id="element-4977" data-mp="2lqsme64j7k8i">
              <dl className="space-y-2" data-id="element-4978" data-mp="2lphlce2h0ql3">
                {[{
              term: 'Method',
              value: request.collection.method
            }, {
              term: 'Delivering to',
              value: request.collection.deliveryTo ?? 'Collection only'
            }, {
              term: 'Tracking',
              value: request.collection.tracking ?? 'Not yet available'
            }, {
              term: 'Proof',
              value: request.collection.proof ?? 'Recorded on collection'
            }].map(row => <div key={row.term} className="flex items-start gap-2" data-id="element-4979" data-mp="tasswkmppzfw">
                    <TruckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-subtle" data-id="element-4980" data-mp="ob6ew8z28f61" />
                    <div data-id="element-4981" data-mp="odj5tbmspzv8">
                      <dt className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-4982" data-mp="12apjirsnx8dl">
                        {row.term}
                      </dt>
                      <dd className="text-[13px] leading-6 text-ink" data-id="element-4983" data-mp="y79r67nmlu6p">{row.value}</dd>
                    </div>
                  </div>)}
              </dl>
              <p className="mt-2 text-[12px] leading-5 text-ink-muted" data-id="element-4984" data-mp="172z6q02e7uc6">
                Your delivery address is shown to the courier only, and never to other students.
              </p>
            </Panel>}

          {request.verification && <Panel title="Verification record" data-id="element-4985" data-mp="20lx3ixwz6asc">
              <p className="flex flex-wrap items-center gap-2" data-id="element-4986" data-mp="20m5euldg981f">
                <Badge tone="ok" icon={<ShieldCheckIcon className="h-3.5 w-3.5" data-id="element-4988" data-mp="24lxtnftfsaf8" />} data-id="element-4987" data-mp="24ninf37xfpc1">
                  Verified
                </Badge>
                <span className="cz-num text-[12px] text-ink-subtle" data-id="element-4989" data-mp="1qmy6kclko3l6">
                  {request.verification.reference}
                </span>
              </p>
              <p className="mt-2 text-[13px] leading-6 text-ink" data-id="element-4990" data-mp="1gxejsvuqnpl1">
                Submitted by {request.verification.submittedBy} on {request.verification.verifiedOn}.
              </p>
              <p className="mt-2.5 text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-4991" data-mp="17x8alm62pbro">
                A verifier sees
              </p>
              <ul className="mt-1 space-y-1" data-id="element-4992" data-mp="1cwb4qbkilxkv">
                {request.verification.exposes.map(item => <li key={item} className="flex items-start gap-1.5 text-[12px] leading-5 text-ink" data-id="element-4993" data-mp="y0vhoqicr1xz">
                    <BadgeCheckIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ok-700" data-id="element-4994" data-mp="12zznd762ihjk" /> {item}
                  </li>)}
              </ul>
              <p className="mt-2.5 text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-4995" data-mp="21ar5tmtvkqvn">
                Never exposed
              </p>
              <ul className="mt-1 space-y-1" data-id="element-4996" data-mp="1wgn016wciow5">
                {request.verification.withheld.map(item => <li key={item} className="flex items-start gap-1.5 text-[12px] leading-5 text-ink-muted" data-id="element-4997" data-mp="1mktlxj2zanc9">
                    <LockIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-4998" data-mp="1ijgl5neqtac7" /> {item}
                  </li>)}
              </ul>
            </Panel>}

          <Panel title="Linked notes" data-id="element-4999" data-mp="1dm7zvdyasq6q">
            {linkedNotes.length === 0 ? <p className="text-[13px] text-ink-muted" data-id="element-5000" data-mp="18mvd0t7ffj45">
                Nothing linked yet. Keep instructions, deadlines and correspondence beside the request.
              </p> : <ul className="space-y-2" data-id="element-5001" data-mp="32zuy31ukt5it">
                {linkedNotes.map(note => <li key={note.id} data-id="element-5002" data-mp="3ai1i4rler9u2">
                    <Link to="/notes" className="flex items-start gap-2 text-[13px] font-medium text-brand-800 hover:underline" data-id="element-5003" data-mp="3agqb8iwo1lyq">
                      <StickyNoteIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-5004" data-mp="3kaqgckry6r7g" />
                      {note.title}
                    </Link>
                  </li>)}
              </ul>}
            <Button size="sm" variant="secondary" className="mt-2.5" icon={<StickyNoteIcon className="h-3.5 w-3.5" data-id="element-5006" data-mp="31hd2xtka33fl" />} onClick={() => {
            const id = createNote({
              title: `${service.name} — ${request.id}`,
              academicLink: {
                kind: 'Service request',
                label: request.id
              }
            });
            onUpdate({
              noteIds: [...request.noteIds, id]
            });
          }} data-id="element-5005" data-mp="2witcpgynj5hf">
              New note for this request
            </Button>
            <p className="mt-2 text-[12px] leading-5 text-ink-muted" data-id="element-5007" data-mp="2hs21g1uv7rg0">
              A linked note stays yours. Linking never grants the provider, referee or institution access
              to it.
            </p>
          </Panel>
        </div>
      </div>
    </div>;
}